import { ChangeEvent, useState } from 'react';
import Input from '../../../../components/common/Input/Input';
import { Button } from '../../../../components/ui/Button/Button';
import S from './EditAudioFilenameModalStyles';

const ALLOWED_EXTENSIONS = ['.aac', '.mp3'];
const MIN_NAME_LENGTH = 3;
const MAX_NAME_LENGTH = 34;

function getBaseNameBeforeExtension(name: string): string {
  const trimmed = name.trim();
  const lower = trimmed.toLowerCase();
  const ext = ALLOWED_EXTENSIONS.find((e) => lower.endsWith(e));
  return ext ? trimmed.slice(0, -ext.length) : trimmed;
}

function hasAllowedExtension(name: string): boolean {
  const lower = name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

type Props = {
  initialFilename?: string;
  existingNames?: Set<string>;
  onClose: () => void;
  onUpdateFilename?: (newFilename: string) => void;
};

function EditAudioFilenameModal({
  initialFilename = '',
  existingNames = new Set(),
  onClose,
  onUpdateFilename,
}: Props): JSX.Element {
  const [filename, setFilename] = useState(initialFilename);
  const [formatError, setFormatError] = useState<string | null>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFilename(e.target.value);
    setFormatError(null);
  };

  const handleSubmit = (): void => {
    const trimmed = filename.trim();
    if (!trimmed) return;
    const baseName = getBaseNameBeforeExtension(trimmed);
    if (baseName.length < MIN_NAME_LENGTH) {
      setFormatError(`Name must be at least ${MIN_NAME_LENGTH} characters`);
      return;
    }
    if (baseName.length > MAX_NAME_LENGTH) {
      setFormatError('Name must be at most 30 characters');
      return;
    }
    if (!hasAllowedExtension(trimmed)) {
      setFormatError('Filename must end with .aac or .mp3');
      return;
    }
    if (existingNames.has(trimmed.toLowerCase())) {
      setFormatError('Filename already exists');
      return;
    }
    setFormatError(null);
    onUpdateFilename?.(trimmed);
    onClose();
  };

  const baseName = getBaseNameBeforeExtension(filename.trim());
  const isValid = baseName.length >= MIN_NAME_LENGTH && baseName.length <= MAX_NAME_LENGTH;

  return (
    <S.Wrapper>
      <Input
        name="filename"
        label="Filename"
        type="text"
        maxLength={34}
        value={filename}
        placeholder="e.g. Welcome_Message.aac"
        onChange={handleInputChange}
        error={formatError ?? undefined}
      />
      <S.Actions>
        <Button
          padding="7px 24px"
          disabled={!isValid}
          onClick={handleSubmit}
        >
          Update filename
        </Button>
      </S.Actions>
    </S.Wrapper>
  );
}

export default EditAudioFilenameModal;
